import type { EChartsOption } from 'echarts';
import dayjs from 'dayjs';
import { keepTwoOrLine } from '@/utils/utils';

/** 趋势图数据项（日报）：[timestamp, current, lastMonth, lastYear] */
type TrendTimeDataItem = [number, number, number, number];

/** 趋势图数据项（月报）：{ time: string; current: number; lastMonth: number; lastYear: number } */
export interface TrendCategoryItem {
  day: string; // X轴显示的日期（如 "01"、"31"）
  currentDate: string; // 本月日期 MM-DD（如 "01-15"）
  lastMonthDate: string; // 上月日期 MM-DD（如 "12-15"）
  lastYearDate: string; // 去年同期日期 YYYY-MM-DD（如 "2025-01-15"）
  current: number | null;
  lastMonth: number | null;
  lastYear: number | null;
}

/** 日报趋势图配置（使用 time 类型 x 轴，按小时展示） */
export const getTrendChartOptionDaily = (
  data: TrendTimeDataItem[],
  startTime: number,
  endTime: number,
  unit: string = '元'
): EChartsOption => {
  return {
    backgroundColor: 'transparent',
    color: ['#16DD8E', '#FFCB47', '#00ADFF'],
    legend: {
      right: 0,
      top: 0,
      itemWidth: 16,
      itemHeight: 4,
      data: [
        { name: '本月', icon: 'rect' },
        { name: '上月', icon: 'rect' },
        { name: '去年同期', icon: 'path://M0 2 L4 2 L4 6 L0 6 Z M6 2 L10 2 L10 6 L6 6 Z M12 2 L16 2 L16 6 L12 6 Z' },
      ],
    },
    tooltip: {
      trigger: 'axis',
      formatter: (params: any) => {
        const paramsArr = Array.isArray(params) ? params : [params];
        const axisValue = paramsArr[0]?.value?.[0] || paramsArr[0]?.axisValue;
        const timeStr = dayjs(axisValue).format('YYYY-MM-DD HH:mm');
        let html = `<div style="font-weight:bold;margin-bottom:8px;">${timeStr}</div>`;
        paramsArr.forEach((p: any) => {
          const value = p.value?.[1] ?? p.value;
          const seriesName = p.seriesName;
          const marker = p.marker;
          html += `<div style="margin-top:8px;width:220px;"><div style="display:flex;justify-content:space-between;align-items:center;"><div><span>${marker}</span>${seriesName}</div><div>${keepTwoOrLine(
            value
          )} ${unit}</div></div></div>`;
        });
        return html;
      },
    } as any,
    xAxis: {
      type: 'time',
      min: startTime,
      max: endTime,
      axisLabel: {
        formatter: (value: number) => dayjs(value).format('HH:mm'),
      },
    },
    yAxis: {
      type: 'value',
      name: unit,
    },
    series: [
      {
        name: '本月',
        type: 'line',
        data: data.map(d => [d[0], d[1]]),
        smooth: true,
        symbol: 'circle',
        symbolSize: 4,
        lineStyle: { width: 2 },
      },
      {
        name: '上月',
        type: 'line',
        data: data.map(d => [d[0], d[2]]),
        smooth: true,
        symbol: 'circle',
        symbolSize: 4,
        lineStyle: { width: 2, type: 'dashed' },
      },
      {
        name: '去年同期',
        type: 'line',
        data: data.map(d => [d[0], d[3]]),
        smooth: true,
        symbol: 'circle',
        symbolSize: 4,
        lineStyle: { width: 2, type: 'dotted' },
      },
    ],
  };
};

/** 月报趋势图配置（使用 category 类型 x 轴，按天展示） */
export const getTrendChartOptionMonthly = (data: TrendCategoryItem[], unit: string = '元'): EChartsOption => {
  return {
    backgroundColor: 'transparent',
    color: ['#16DD8E', '#FFCB47', '#00ADFF'],
    legend: {
      right: 0,
      top: 0,
      itemWidth: 16,
      itemHeight: 4,
      data: [
        { name: '本月', icon: 'rect' },
        { name: '上月', icon: 'rect' },
        { name: '去年同期', icon: 'path://M0 2 L4 2 L4 6 L0 6 Z M6 2 L10 2 L10 6 L6 6 Z M12 2 L16 2 L16 6 L12 6 Z' },
      ],
    },
    tooltip: {
      trigger: 'axis',
      extraCssText: 'width: 280px;',
      formatter: (params: any) => {
        const paramsArr = Array.isArray(params) ? params : [params];
        const dataIndex = paramsArr[0]?.dataIndex;
        const item = data[dataIndex];
        if (!item) return '';
        let html = `<div style="font-weight:bold;margin-bottom:8px;">${item.day}号</div>`;
        paramsArr.forEach((p: any) => {
          const value = p.value;
          const seriesName = p.seriesName;
          const marker = p.marker;
          let dateLabel = '';
          if (seriesName === '本月') {
            dateLabel = `（${item.currentDate}）`;
          } else if (seriesName === '上月') {
            dateLabel = `（${item.lastMonthDate}）`;
          } else if (seriesName === '去年同期') {
            dateLabel = `（${item.lastYearDate}）`;
          }
          html += `<div style="margin-top:8px;"><div style="display:flex;justify-content:space-between;align-items:center;"><div><span>${marker}</span>${seriesName}${dateLabel}</div><div>${keepTwoOrLine(
            value
          )} ${unit}</div></div></div>`;
        });
        return html;
      },
    } as any,
    xAxis: {
      type: 'category',
      data: data.map(d => d.day),
      axisLabel: {
        formatter: (value: string) => value,
      },
    },
    yAxis: {
      type: 'value',
      name: unit,
    },
    series: [
      {
        name: '本月',
        type: 'line',
        data: data.map(d => d.current),
        smooth: true,
        symbol: 'circle',
        symbolSize: 4,
        lineStyle: { width: 2 },
      },
      {
        name: '上月',
        type: 'line',
        data: data.map(d => d.lastMonth),
        smooth: true,
        symbol: 'circle',
        symbolSize: 4,
        lineStyle: { width: 2 },
      },
      {
        name: '去年同期',
        type: 'line',
        data: data.map(d => d.lastYear),
        smooth: true,
        symbol: 'circle',
        symbolSize: 4,
        lineStyle: { width: 2, type: 'dashed' },
      },
    ],
  };
};

/** 绿色能源排名进度条图配置 */
interface GreenEnergyRanking {
  name: string;
  percent: number;
}

export const getGreenEnergyRankingOption = (data: GreenEnergyRanking[]): EChartsOption => {
  return {
    backgroundColor: 'transparent',
    color: ['#16DD8E'],
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params: any) => {
        const paramsArr = Array.isArray(params) ? params : [params];
        const p = paramsArr[0];
        return `<div>${p.marker}${p.name}：${p.value}%</div>`;
      },
    } as any,
    xAxis: {
      type: 'value',
      max: 100,
      show: false,
    },
    yAxis: {
      type: 'category',
      data: data.map(r => r.name),
      axisLabel: {
        formatter: (v: string) => (v.length > 12 ? v.slice(0, 12) + '...' : v),
      },
      axisLine: { show: false },
      axisTick: { show: false },
    },
    series: [
      {
        type: 'bar',
        data: data.map(r => r.percent),
        barMaxWidth: 8,
        itemStyle: {
          borderRadius: 4,
          color: '#16DD8E',
        },
        showBackground: true,
        backgroundStyle: {
          color: 'rgba(255,255,255,0.05)',
          borderRadius: 4,
        },
      },
    ],
  };
};
